(function () {
  const CHILD_FULL_KEYS = ['Enfant', 'Child name', 'child_name'];
  const CHILD_FIRST_KEYS = ['_child_first_name', 'child_first_name', "Prénom de l'enfant", "Prénom de l’enfant"];
  const CHILD_LAST_KEYS = ['_child_last_name', 'child_last_name', "Nom de l'enfant", "Nom de l’enfant"];
  const STOREFRONT_API_VERSION = '2026-04';
  // Pays par défaut si le pays du visiteur est inconnu (boutique anglaise : pays du marché principal)
  const DEFAULT_DELIVERY_COUNTRY_CODE = 'GB';
  const CART_CREATE_MUTATION = `
    mutation CartCreate($input: CartInput!) {
      cartCreate(input: $input) {
        cart {
          checkoutUrl
        }
        userErrors {
          field
          message
        }
      }
    }
  `;
  let checkoutRedirectInProgress = false;

  function getProperty(properties, keys) {
    if (!properties) return '';
    for (const key of keys) {
      if (Object.prototype.hasOwnProperty.call(properties, key) && properties[key]) {
        return String(properties[key]).trim();
      }
    }
    return '';
  }

  function splitFullName(fullName) {
    const parts = String(fullName || '').trim().split(/\s+/).filter(Boolean);
    if (parts.length < 2) return null;

    return {
      firstName: parts.shift(),
      lastName: parts.join(' '),
    };
  }

  function getChildNameFromCart(cart) {
    if (!cart || !Array.isArray(cart.items)) return null;

    const readChildName = (item) => {
      const properties = item.properties || {};
      const firstName = getProperty(properties, CHILD_FIRST_KEYS);
      const lastName = getProperty(properties, CHILD_LAST_KEYS);

      if (firstName && lastName) {
        return { firstName, lastName };
      }

      const fullName = getProperty(properties, CHILD_FULL_KEYS);
      return splitFullName(fullName);
    };

    for (const item of cart.items) {
      if (!item || !item.selling_plan_allocation) continue;
      const childName = readChildName(item);
      if (childName) return childName;
    }

    return null;
  }

  function buildDefaultCheckoutUrl() {
    const root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root ? window.Shopify.routes.root : '/';
    return new URL(`${root}checkout`, window.location.origin).toString();
  }

  function getCartJsonUrl() {
    const cartUrl = window.routes && window.routes.cart_url ? window.routes.cart_url : '/cart';
    return cartUrl.endsWith('.js') ? cartUrl : `${cartUrl}.js`;
  }

  function toStorefrontId(type, id) {
    const value = String(id || '');
    return value.startsWith('gid://shopify/') ? value : `gid://shopify/${type}/${value}`;
  }

  function toAttributes(properties) {
    if (!properties) return [];

    return Object.entries(properties)
      .filter(([, value]) => value !== null && typeof value !== 'undefined' && value !== '')
      .map(([key, value]) => ({
        key,
        value: String(value),
      }));
  }

  // Pays du visiteur (marché Shopify actif), pour que l'adresse pré-remplie et le marché du checkout correspondent
  function getCountryCode() {
    const country = window.Shopify && typeof window.Shopify.country === 'string' ? window.Shopify.country.trim().toUpperCase() : '';
    return /^[A-Z]{2}$/.test(country) ? country : DEFAULT_DELIVERY_COUNTRY_CODE;
  }

  function buildStorefrontCartInput(cart, childName) {
    const countryCode = getCountryCode();
    const input = {
      buyerIdentity: {
        countryCode,
      },
      lines: cart.items.map((item) => {
        const line = {
          merchandiseId: toStorefrontId('ProductVariant', item.variant_id || item.id),
          quantity: item.quantity || 1,
        };
        const attributes = toAttributes(item.properties);
        const sellingPlanId = item.selling_plan_allocation
          && item.selling_plan_allocation.selling_plan
          && item.selling_plan_allocation.selling_plan.id;

        if (attributes.length) line.attributes = attributes;
        if (sellingPlanId) {
          line.sellingPlanId = toStorefrontId('SellingPlan', sellingPlanId);
        }

        return line;
      }),
      delivery: {
        addresses: [
          {
            address: {
              deliveryAddress: {
                countryCode,
                firstName: childName.firstName,
                lastName: childName.lastName,
              },
            },
            selected: true,
            oneTimeUse: true,
            validationStrategy: 'COUNTRY_CODE_ONLY',
          },
        ],
      },
    };

    const attributes = toAttributes(cart.attributes);
    const discountCodes = Array.isArray(cart.discount_codes)
      ? cart.discount_codes.map((discount) => discount && discount.code).filter(Boolean)
      : [];

    if (attributes.length) input.attributes = attributes;
    if (cart.note) input.note = String(cart.note);
    if (discountCodes.length) input.discountCodes = discountCodes;

    return input;
  }

  async function createCheckoutUrl(cart, childName) {
    const response = await fetch(`/api/${STOREFRONT_API_VERSION}/graphql.json`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        query: CART_CREATE_MUTATION,
        variables: {
          input: buildStorefrontCartInput(cart, childName),
        },
      }),
    });
    const payload = await response.json();
    const result = payload && payload.data && payload.data.cartCreate;
    const userErrors = result && Array.isArray(result.userErrors) ? result.userErrors : [];
    const checkoutUrl = result && result.cart && result.cart.checkoutUrl;

    if (!response.ok || (payload && payload.errors) || userErrors.length || !checkoutUrl) {
      console.warn('[new-main-product-checkout] cartCreate failed', { status: response.status, errors: payload && payload.errors, userErrors });
      throw new Error(userErrors[0] ? userErrors[0].message : 'Unable to create checkout');
    }

    return checkoutUrl;
  }

  async function redirectToCheckout(event) {
    if (checkoutRedirectInProgress) return;
    checkoutRedirectInProgress = true;

    if (event) {
      event.preventDefault();
      event.stopPropagation();
      if (typeof event.stopImmediatePropagation === 'function') {
        event.stopImmediatePropagation();
      }
    }

    try {
      const response = await fetch(getCartJsonUrl(), {
        headers: { Accept: 'application/json' },
      });
      const cart = await response.json();
      const childName = getChildNameFromCart(cart);

      if (childName) {
        window.location.href = await createCheckoutUrl(cart, childName);
        return;
      }

      window.location.href = buildDefaultCheckoutUrl();
    } catch (error) {
      console.warn('[new-main-product-checkout] child name not injected, default checkout used', error);
      window.location.href = buildDefaultCheckoutUrl();
    }
  }

  document.addEventListener(
    'click',
    (event) => {
      const checkoutButton = event.target.closest('button[name="checkout"], input[name="checkout"], .cart__checkout-button');
      if (!checkoutButton) return;
      redirectToCheckout(event);
    },
    true
  );

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      if (event.submitter && event.submitter.name !== 'checkout') return;
      if (!form.matches('form[action="/cart"], form[action="/cart/"], #cart')) return;
      redirectToCheckout(event);
    },
    true
  );
})();
